import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Select, InputNumber, DatePicker, Spin, Empty } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { usePermisos } from "../../context/PermisosContext";
import { apiCargosInstance, authHeaderCargos } from "../../redux/actions/cargos/cargos";

import ActividadTab from "./ActividadTab";

import "./CargosPage.css";

dayjs.locale("es");

const fmtFecha = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const money = (n) =>
  n == null || n === "" ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CargoDetallePage() {
  const { idCargo } = useParams();
  const { perm } = usePermisos() || { perm: () => true };

  const [cargo, setCargo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("info");

  const [formasPago, setFormasPago] = useState([]);
  const [pagoOpen, setPagoOpen] = useState(false);
  const [savingPago, setSavingPago] = useState(false);
  const [nuevoPago, setNuevoPago] = useState({});

  const fetchCargo = useCallback(() => {
    setLoading(true);
    apiCargosInstance
      .get(`/cargos/${idCargo}`, { headers: authHeaderCargos() })
      .then((res) => setCargo(res.data))
      .catch(() => toast.error("No se pudo cargar el cargo"))
      .finally(() => setLoading(false));
  }, [idCargo]);

  useEffect(() => { fetchCargo(); }, [fetchCargo]);

  useEffect(() => {
    apiCargosInstance
      .get("/formas-pago", { headers: authHeaderCargos() })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.items ?? [];
        setFormasPago(data.map((f) => ({ label: f.nombre, value: f.id ?? f.id_forma_pago })));
      })
      .catch(() => {});
  }, []);

  const handleAddPago = async () => {
    if (!nuevoPago.id_forma_pago || nuevoPago.monto == null) {
      toast.warning("Selecciona la forma de pago e ingresa el monto");
      return;
    }
    setSavingPago(true);
    try {
      const payload = {
        id_forma_pago: nuevoPago.id_forma_pago,
        monto: nuevoPago.monto,
        fecha_pago: nuevoPago.fecha_pago ? nuevoPago.fecha_pago.format("YYYY-MM-DD") : undefined,
      };
      await apiCargosInstance.post(`/cargos/${idCargo}/pagos`, payload, { headers: authHeaderCargos() });
      toast.success("Pago registrado");
      setPagoOpen(false);
      setNuevoPago({});
      fetchCargo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar el pago");
    } finally {
      setSavingPago(false);
    }
  };

  if (loading) {
    return (
      <div className="crgd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!cargo) {
    return (
      <div className="crgd-main" style={{ padding: 40 }}>
        <Empty description="Cargo no encontrado" />
      </div>
    );
  }

  const pagos = cargo.pagos || [];
  const totalPagado = cargo.total_pagado != null
    ? Number(cargo.total_pagado)
    : pagos.reduce((a, p) => a + parseFloat(p.monto || 0), 0);
  const montoBase = Number(cargo.monto_base ?? cargo.monto_final ?? cargo.monto_estimado ?? 0);
  const saldo = cargo.saldo != null ? Number(cargo.saldo) : Math.max(0, montoBase - totalPagado);
  const liquidado = saldo <= 0;

  return (
    <div className="crgd-main">
      <div className="crgd-content">
        <div className="crgd-header-card">
          <div className="crgd-header-top">
            <div>
              <h1 className="crgd-name">
                CARGO #{cargo.id ?? cargo.id_cargo} — {cargo.paciente_nombre || `Paciente #${cargo.id_paciente}`}
              </h1>
              <div className="crgd-meta">
                <span className="crgd-meta-item"><i className="fa-solid fa-calendar" /> {fmtFecha(cargo.fecha || cargo.datetime)}</span>
                <span className="crgd-meta-item"><i className="fa-solid fa-tag" /> {cargo.concepto || "Sin concepto"}</span>
              </div>
            </div>
          </div>

          <div className="crgd-tabs">
            <button className={`crgd-tab-btn ${activeTab === "info" ? "crgd-tab-btn-active" : ""}`} onClick={() => setActiveTab("info")}>
              <i className="fa-solid fa-file-lines" /> Información
            </button>
            <button className={`crgd-tab-btn ${activeTab === "pagos" ? "crgd-tab-btn-active" : ""}`} onClick={() => setActiveTab("pagos")}>
              <i className="fa-solid fa-money-bill-wave" /> Pagos ({pagos.length})
            </button>
            <button className={`crgd-tab-btn ${activeTab === "actividad" ? "crgd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {activeTab === "info" && (
          <div className="crgd-bento">
            <div className="crgd-card">
              <div className="crgd-card-header">
                <div className="crgd-card-icon"><i className="fa-solid fa-file-invoice-dollar" /></div>
                <h2 className="crgd-card-title">Información del cargo</h2>
              </div>
              <div className="crgd-field-grid">
                <div>
                  <span className="crgd-field-label">Paciente</span>
                  <span className="crgd-field-value">{cargo.paciente_nombre || `Paciente #${cargo.id_paciente}`}</span>
                </div>
                <div>
                  <span className="crgd-field-label">Concepto</span>
                  <span className="crgd-field-value">{cargo.concepto || "—"}</span>
                </div>
                <div>
                  <span className="crgd-field-label">Servicio</span>
                  <span className="crgd-field-value">{cargo.servicio_nombre || "—"}</span>
                </div>
                <div>
                  <span className="crgd-field-label">Cita relacionada</span>
                  <span className="crgd-field-value">{cargo.id_cita ? `#${cargo.id_cita}` : "—"}</span>
                </div>
                <div>
                  <span className="crgd-field-label">Fecha</span>
                  <span className="crgd-field-value">{fmtFecha(cargo.fecha || cargo.datetime)}</span>
                </div>
                <div>
                  <span className="crgd-field-label">Estado</span>
                  <span className="crgd-field-value" style={{ color: liquidado ? "#15803d" : "#b45309" }}>
                    {liquidado ? "Liquidado" : "Pendiente"}
                  </span>
                </div>
              </div>
            </div>

            <div className="crgd-card">
              <div className="crgd-card-header">
                <div className="crgd-card-icon"><i className="fa-solid fa-coins" /></div>
                <h2 className="crgd-card-title">Totales</h2>
              </div>
              <div className="crgd-totales-row">
                <div>
                  <span className="crgd-field-label">Monto estimado</span>
                  <span className="crgd-field-value">{money(cargo.monto_estimado)}</span>
                </div>
                <div>
                  <span className="crgd-field-label">Monto final</span>
                  <span className="crgd-field-value">{money(cargo.monto_final)}</span>
                </div>
                <div>
                  <span className="crgd-field-label">Total pagado</span>
                  <span className="crgd-field-value" style={{ color: "#15803d" }}>{money(totalPagado)}</span>
                </div>
                <div>
                  <span className="crgd-field-label">Saldo pendiente</span>
                  <span className="crgd-field-value" style={{ color: liquidado ? "#15803d" : "#b45309" }}>{money(saldo)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "pagos" && (
          <div className="crgd-card">
            <div className={`crgd-saldo-banner ${liquidado ? "is-liquidado" : ""}`}>
              <div>
                <span className="crgd-field-label" style={{ marginBottom: 0 }}>Saldo pendiente</span>
                <div className="crgd-saldo-value">{money(saldo)}</div>
              </div>
              {!liquidado && perm("cargos_pagos", "insertar") && (
                <Button
                  type="primary"
                  icon={<i className="fa-solid fa-plus" />}
                  onClick={() => setPagoOpen(true)}
                  style={{ background: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
                >
                  Registrar pago
                </Button>
              )}
            </div>

            <div className="crgd-card-header">
              <div className="crgd-card-icon"><i className="fa-solid fa-money-bill-wave" /></div>
              <h2 className="crgd-card-title">Historial de pagos</h2>
            </div>

            {pagos.length === 0 ? (
              <Empty description="Todavía no hay pagos registrados" />
            ) : (
              <div className="crgd-pagos-list">
                {pagos.map((p) => (
                  <div key={p.id ?? p.id_pago} className="crgd-pago-row">
                    <div>
                      <span className="crgd-field-label">Forma de pago</span>
                      <div className="crgd-field-value">{p.forma_pago_nombre || p.forma_pago || "—"}</div>
                    </div>
                    <div>
                      <span className="crgd-field-label">Monto</span>
                      <div className="crgd-field-value">{money(p.monto)}</div>
                    </div>
                    <div>
                      <span className="crgd-field-label">Fecha</span>
                      <div className="crgd-field-value">{fmtFecha(p.fecha_pago || p.datetime)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "actividad" && (
          <ActividadTab
            titulo="Actividad del cargo"
            fetcher={() => apiCargosInstance.get(`/cargos/${idCargo}/actividad`, { headers: authHeaderCargos() })}
          />
        )}
      </div>

      <Modal
        open={pagoOpen}
        onCancel={() => setPagoOpen(false)}
        onOk={handleAddPago}
        okText="Registrar pago"
        cancelText="Cancelar"
        title="Registrar pago"
        confirmLoading={savingPago}
        centered
        okButtonProps={{ style: { background: "var(--brand-primary)", borderColor: "var(--brand-primary)" } }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="crgd-saldo-banner" style={{ marginBottom: 0 }}>
            <div>
              <span className="crgd-field-label" style={{ marginBottom: 0 }}>Saldo pendiente</span>
              <div className="crgd-saldo-value">{money(saldo)}</div>
            </div>
          </div>
          <Select
            placeholder="Forma de pago"
            options={formasPago}
            value={nuevoPago.id_forma_pago}
            onChange={(v) => setNuevoPago((f) => ({ ...f, id_forma_pago: v }))}
          />
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Monto"
            min={0}
            max={saldo > 0 ? saldo : undefined}
            precision={2}
            addonBefore="$"
            value={nuevoPago.monto}
            onChange={(v) => setNuevoPago((f) => ({ ...f, monto: v }))}
          />
          <DatePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            placeholder="Fecha de pago (opcional)"
            value={nuevoPago.fecha_pago}
            onChange={(v) => setNuevoPago((f) => ({ ...f, fecha_pago: v }))}
          />
        </div>
      </Modal>
    </div>
  );
}
